// src/pages/public/PublicInvoicePay.js
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
    Box,
    Card,
    CardContent,
    Container,
    Typography,
    Button,
    CircularProgress,
    Stack,
    Divider,
    TextField,
    MenuItem,
} from '@mui/material'
import { http } from '../../api/http'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'

const SUPPORTED_PAYER = ['USD', 'GBP', 'EUR', 'CAD', 'AUD', 'SLE', 'LRD', 'GNF']

function toNumber(v) {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

function fmtMoney(n) {
    const x = Number(n)
    if (!Number.isFinite(x)) return '0.00'
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x)
}

const DIASPORA_CODES = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU']

const countryCodeFromCurrency = (currency) => {
    switch (String(currency || '').toUpperCase()) {
        case 'LRD':
            return 'LR'
        case 'GNF':
            return 'GN'
        case 'GMD':
            return 'GM'
        case 'SLE':
            return 'SL'
        default:
            return 'US'
    }
}

const firstItemDescription = (invoice) => {
    const first = Array.isArray(invoice?.items) ? invoice.items[0] : null
    return first?.description || invoice?.note || 'Invoice payment'
}

export default function PublicInvoicePay() {
    const { t } = useLanguage()
    const { user } = useAuth()

    const { token } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const autoPayStarted = useRef(false)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [raw, setRaw] = useState(null)

    const [payerCurrency, setPayerCurrency] = useState('USD')
    const [paying, setPaying] = useState(false)

    const savedUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('session_user') || '{}')
        } catch {
            return {}
        }
    }, [user])

    const inv = useMemo(() => {
        if (!raw) return null
        if (raw?.invoice) return raw.invoice
        if (raw?.data && (raw.data.invoice_no || raw.data.public_token || raw.data.items)) return raw.data
        return raw
    }, [raw])

    const invCurrency = useMemo(() => {
        const c =
            inv?.currency ||
            inv?.merchant?.default_currency ||
            raw?.merchant?.default_currency ||
            raw?.invoice_currency ||
            'USD'
        return String(c).toUpperCase()
    }, [inv, raw])

    const invTotal = useMemo(() => {
        const t0 = toNumber(inv?.total)
        if (t0 > 0) return t0

        const items = inv?.items || []
        const sum = items.reduce((acc, it) => {
            const line =
                it?.line_total != null
                    ? toNumber(it.line_total)
                    : toNumber(it?.qty) * toNumber(it?.unit_price)
            return acc + line
        }, 0)

        return sum
    }, [inv])

    const conversion = useMemo(() => {
        const payer_total =
            raw?.payer_total ??
            raw?.conversion?.payer_total ??
            raw?.conversion?.amount ??
            null

        const payer_currency =
            raw?.payer_currency ??
            raw?.conversion?.payer_currency ??
            payerCurrency

        return {
            payer_currency: String(payer_currency || payerCurrency).toUpperCase(),
            payer_total: payer_total === null ? null : toNumber(payer_total),
            has: payer_total !== null && Number.isFinite(Number(payer_total)),
        }
    }, [raw, payerCurrency])

    // 1 payerCurrency = X invoiceCurrency
    const fxRate = useMemo(() => {
        if (!conversion.has) return null
        const payer = toNumber(conversion.payer_total)
        if (!payer || payer <= 0) return null
        return invTotal / payer
    }, [conversion.has, conversion.payer_total, invTotal])

    const theyReceive = useMemo(() => {
        if (!invTotal || invTotal <= 0) return 0
        return invTotal
    }, [invTotal])

    const paymentReviewState = useMemo(() => {
        if (!inv || !invTotal) return null

        const payerCountry = String(user?.country || savedUser?.country || 'US').toUpperCase()
        const isDiaspora = DIASPORA_CODES.includes(payerCountry)
        const invoiceNumber = inv?.invoice_no || token
        const publicToken = inv?.public_token || token
        const merchantName = inv?.merchant?.business_name || inv?.merchant_name || 'MojoPayr Merchant'

        return {
            codeFlow: true,
            codeRecord: {
                type: 'invoice',
                code: invoiceNumber,
                invoiceNumber,
                publicToken,
                paymentReference: invoiceNumber,
                merchantName,
                merchantLocation: inv?.merchant?.country || '',
                customerName: inv?.customer_name || 'Customer',
                accountNumber: inv?.customer_email || inv?.customer_phone || invoiceNumber,
                description: firstItemDescription(inv),
                amount: invTotal,
                localCurrency: invCurrency,
                destinationCountry: countryCodeFromCurrency(invCurrency),
            },
            residency: isDiaspora ? 'DIASPORA' : 'LOCAL',
            residentCountry: payerCountry,
        }
    }, [inv, invCurrency, invTotal, savedUser, token, user])

    useEffect(() => {
        let mounted = true

        async function load() {
            setLoading(true)
            setError('')
            try {
                const res = await http.get(`/public/invoices/${token}`, {
                    params: { payer_currency: payerCurrency },
                })
                if (!mounted) return
                setRaw(res.data)
            } catch (e) {
                if (!mounted) return
                setError(e?.response?.data?.message || t('publicInvoice.errors.notFound', null, 'Invoice not found.'))
            } finally {
                if (mounted) setLoading(false)
            }
        }

        if (token) load()

        return () => {
            mounted = false
        }
    }, [token, payerCurrency, t])

    const invoiceReturnPath = () => {
        const params = new URLSearchParams(searchParams)
        params.set('pay', '1')
        params.set('payer_currency', payerCurrency)
        return `${location.pathname}?${params.toString()}`
    }

    useEffect(() => {
        if (autoPayStarted.current || loading || error || !paymentReviewState) return
        const shouldAutoPay =
            searchParams.get('pay') === '1' ||
            String(searchParams.get('step') || '').toLowerCase() === 'pay'
        if (!shouldAutoPay) return

        autoPayStarted.current = true
        const token = localStorage.getItem('token')
        if (!user && !token) {
            navigate(`/login?returnTo=${encodeURIComponent(invoiceReturnPath())}`, { replace: true })
            return
        }

        navigate('/payment/confirm', { replace: true, state: paymentReviewState })
    }, [error, loading, navigate, paymentReviewState, searchParams, user])

    const startPay = async () => {
        if (!token) return
        setPaying(true)
        try {
            // Do not send fee_estimate anymore
            const res = await http.post(`/public/invoices/${token}/pay-intent`, {
                payer_currency: payerCurrency,
            })

            const payUrl = res?.data?.pay_url
            if (payUrl) {
                const hasToken = Boolean(localStorage.getItem('token') || user)
                if (!hasToken) {
                    navigate(`/login?returnTo=${encodeURIComponent(invoiceReturnPath())}`)
                    return
                }
                if (paymentReviewState) {
                    navigate('/payment/confirm', { state: paymentReviewState })
                    return
                }
                window.location.href = payUrl
                return
            }

            setError(t('publicInvoice.errors.noPayUrl', null, 'Payment setup is missing (no pay_url returned).'))
        } catch (e) {
            setError(e?.response?.data?.message || t('publicInvoice.errors.startFailed', null, 'Failed to start payment.'))
        } finally {
            setPaying(false)
        }
    }

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 950 }}>
                    {t('publicInvoice.title', null, 'Pay Invoice')}
                </Typography>
                <Typography sx={{ opacity: 0.75, mt: 0.5 }}>
                    {t('publicInvoice.subtitle', null, 'Review the invoice details and pay securely.')}
                </Typography>
            </Box>

            <Card>
                <CardContent>
                    {loading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography sx={{ opacity: 0.8 }}>
                                {t('publicInvoice.loading', null, 'Loading invoice...')}
                            </Typography>
                        </Stack>
                    ) : error ? (
                        <Stack spacing={1.5}>
                            <Typography sx={{ color: 'rgba(248,113,113,0.95)', fontWeight: 900 }}>
                                {error}
                            </Typography>
                            <Box>
                                <Button variant="outlined" onClick={() => navigate('/')}>
                                    {t('publicInvoice.goHome', null, 'Go Home')}
                                </Button>
                            </Box>
                        </Stack>
                    ) : (
                        <>
                            <Stack spacing={0.6}>
                                <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                                    {inv?.merchant?.business_name || inv?.merchant_name || t('publicInvoice.merchantFallback', null, 'Merchant')}
                                </Typography>

                                <Typography sx={{ opacity: 0.8 }}>
                                    {t('publicInvoice.invoiceLabel', null, 'Invoice')}: {inv?.invoice_no || '—'}
                                </Typography>

                                <Typography sx={{ opacity: 0.8 }}>
                                    {t('publicInvoice.customerLabel', null, 'Customer')}: {inv?.customer_name || '—'}
                                </Typography>

                                <Typography sx={{ opacity: 0.8 }}>
                                    {t('publicInvoice.dueLabel', null, 'Due')}: {inv?.due_date ? String(inv?.due_date).slice(0, 10) : '—'}
                                </Typography>
                            </Stack>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 950 }}>
                                        {t('publicInvoice.amountDue', null, 'Amount due')}: {invCurrency} {fmtMoney(invTotal)}
                                    </Typography>

                                    {conversion.has && (
                                        <Typography sx={{ opacity: 0.8, mt: 0.5 }}>
                                            {t('publicInvoice.youPay', null, 'You pay')}: {conversion.payer_currency} {fmtMoney(conversion.payer_total)}
                                        </Typography>
                                    )}
                                </Box>

                                <TextField
                                    select
                                    label={t('publicInvoice.yourCurrency', null, 'Your currency')}
                                    value={payerCurrency}
                                    onChange={(e) => setPayerCurrency(e.target.value)}
                                    sx={{ width: { xs: '100%', sm: 220 } }}
                                >
                                    {SUPPORTED_PAYER.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />

                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography sx={{ opacity: 0.75 }}>
                                        {t('publicInvoice.exchangeRate', null, 'Exchange rate')}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 900 }}>
                                        {fxRate
                                            ? `1 ${conversion.payer_currency} = ${fmtMoney(fxRate)} ${invCurrency}`
                                            : '—'}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography sx={{ opacity: 0.75 }}>
                                        {t('publicInvoice.theyReceive', null, 'They receive')}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 950 }}>
                                        {invCurrency} {fmtMoney(theyReceive)}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />

                            <Typography sx={{ fontWeight: 950, mb: 1 }}>
                                {t('publicInvoice.items', null, 'Items')}
                            </Typography>

                            <Stack spacing={1}>
                                {(inv?.items || []).map((it, idx) => {
                                    const lineTotal =
                                        it?.line_total != null
                                            ? toNumber(it.line_total)
                                            : toNumber(it?.qty) * toNumber(it?.unit_price)

                                    return (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                p: 1.2,
                                                borderRadius: 2,
                                                border: '1px solid rgba(255,255,255,0.10)',
                                                background: 'rgba(255,255,255,0.03)',
                                            }}
                                        >
                                            <Typography sx={{ opacity: 0.9 }}>
                                                {it?.description || t('publicInvoice.itemFallback', null, 'Item')}
                                            </Typography>

                                            <Typography sx={{ fontWeight: 900 }}>
                                                {invCurrency} {fmtMoney(lineTotal)}
                                            </Typography>
                                        </Box>
                                    )
                                })}
                            </Stack>

                            {inv?.note && (
                                <>
                                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />
                                    <Typography sx={{ fontWeight: 950, mb: 0.8 }}>
                                        {t('publicInvoice.note', null, 'Note')}
                                    </Typography>
                                    <Typography sx={{ opacity: 0.8 }}>{inv.note}</Typography>
                                </>
                            )}

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                                <Button variant="outlined" onClick={() => navigate(-1)} disabled={paying}>
                                    {t('common.back', null, 'Back')}
                                </Button>

                                <Button variant="contained" onClick={startPay} disabled={paying}>
                                    {paying
                                        ? t('publicInvoice.starting', null, 'Starting payment...')
                                        : t('publicInvoice.payNow', null, 'Pay now')}
                                </Button>
                            </Stack>
                        </>
                    )}
                </CardContent>
            </Card>
        </Container>
    )
}
